import cn from 'classnames'
import { observer } from 'mobx-react'
import React from 'react'

import { Button, Hierarchy, SubVariant } from '@flowwai-frontend/design-system'
import { CheckCircleFillIcon } from '@flowwai-frontend/design-system/icons'
import { OTPVerification } from '@flowwai-frontend/shared-web/common/ui'
import { UserPINUpdateMode } from '@flowwai-frontend/shared-web/iam/ui'
import { APIStatus, isAPIFetching } from '@flowwai-frontend/shared/api-common'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import { PhoneNumberResponse } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import * as Styles from './styles'

interface Props {
   mode: UserPINUpdateMode
   otp: string
   otpErrorMessage: string | null
   phoneNumber: PhoneNumberResponse | null | undefined
   apiStatus: APIStatus
   onChangeOtp: (otp: string) => void
   onSubmitOtp: (otp: string) => void
   onResendOtp: () => void

   containerClassName?: string
}

const UserPINOTPSubmission = (props: Props): React.ReactElement => {
   const {
      mode,
      otp,
      otpErrorMessage,
      phoneNumber,
      apiStatus,
      onChangeOtp,
      onSubmitOtp,
      onResendOtp
   } = props

   const t = useCustomT('iam:userPIN:otpSubmission')

   const isResetPINMode = mode === 'RESET_PIN'

   const handleSubmitOtp = (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault()
      onSubmitOtp(otp)
   }

   const getMaskedPhoneNumber = (): string => {
      if (!phoneNumber) return ''
      return `${phoneNumber.dialCode} *******${phoneNumber.phoneNumber.slice(
         7
      )}`
   }

   const renderErrorMessage = (): React.ReactElement =>
      otpErrorMessage ? (
         <p className={cn(Styles.errorMessageStyles, 'text-error-600')}>
            {otpErrorMessage}
         </p>
      ) : (
         <p className={cn(Styles.errorMessageStyles)}></p>
      )

   return (
      <form onSubmit={handleSubmitOtp} className={props.containerClassName}>
         <div className='flex justify-center items-center mb-4'>
            <CheckCircleFillIcon height={48} width={48} />
         </div>

         <OTPVerification
            otpValue={otp}
            setOTPValue={onChangeOtp}
            handleResend={onResendOtp}
            otpErrorMessage={otpErrorMessage ?? undefined}
            headerText={
               isResetPINMode ? t('verifyToResetTitle') : t('verifyToSetTitle')
            }
            hideSeparator
            otpFieldValidationConfig={{
               otpDestinationType: 'PHONE_NUMBER',
               otpDestination: getMaskedPhoneNumber()
            }}
            timerDurationInSecs={60}
         />

         <Button
            size='Small'
            hierarchy={Hierarchy.Primary}
            subVariant={SubVariant.Primary}
            type='submit'
            className='w-full mt-6'
            isLoading={isAPIFetching(apiStatus)}
         >
            {isResetPINMode ? t('verifyToReset') : t('verifyToSet')}
         </Button>
      </form>
   )
}

export default observer(UserPINOTPSubmission)
